// Drag the call panel anywhere, and let it settle against an edge.
//
// The panel floats over the whole console precisely so an operator can keep working while they
// talk — but a fixed corner is the wrong corner about half the time: it lands on the map, the
// roster, or the very camera tile they are trying to read. So they move it, and it snaps to a side
// rather than sitting wherever it was dropped, which keeps it out of the middle of the screen.
//
// Pointer events, not mouse events: the same code then works for touch and pen, and pointer capture
// keeps the drag alive when the cursor leaves the window mid-move.

import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react'

import { clampToViewport, defaultPosition, snapToEdge, type Point } from './panel-position'

/**
 * Remembered for the session, per browser.
 *
 * An operator who moved the panel out of the way of their map means it for the next call too. The
 * stored value is a position, not a corner, and it is re-clamped on read — a window that has since
 * been made smaller must not restore a panel off-screen.
 */
const STORAGE_KEY = 'gisify.sos.callPanelPos'

function readStored(): Point | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)

    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<Point>

    return typeof parsed.left === 'number' && typeof parsed.top === 'number'
      ? { left: parsed.left, top: parsed.top }
      : null
  } catch {
    // Private windows and blocked site data throw on access. A forgotten position is a nuisance;
    // a panel that fails to render during an emergency is not acceptable.
    return null
  }
}

function writeStored(pos: Point): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pos))
  } catch {
    // Same reasoning.
  }
}

export interface DraggablePanel {
  /** Attach to the panel's own element. */
  panelRef: (el: HTMLDivElement | null) => void
  /** Inline position. Null until measured, so the panel never flashes in the wrong place. */
  style: CSSProperties
  /** Spread onto the drag handle — the header, not the whole panel. */
  handleProps: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void
    style: CSSProperties
  }
  dragging: boolean
}

export function useDraggablePanel(): DraggablePanel {
  const elRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<Point | null>(null)
  const [dragging, setDragging] = useState(false)
  // Where in the panel the pointer grabbed it, so the panel does not jump to the cursor.
  const grabRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 })
  const observerRef = useRef<ResizeObserver | null>(null)

  const viewport = () => ({ width: window.innerWidth, height: window.innerHeight })
  const size = () => ({
    width: elRef.current?.offsetWidth ?? 0,
    height: elRef.current?.offsetHeight ?? 0,
  })

  const panelRef = useCallback((el: HTMLDivElement | null) => {
    elRef.current = el

    observerRef.current?.disconnect()
    observerRef.current = null

    if (!el) return

    const panel = { width: el.offsetWidth, height: el.offsetHeight }
    const stored = readStored()

    setPos(stored ? clampToViewport(stored, panel, viewport()) : defaultPosition(panel, viewport()))

    if (typeof ResizeObserver === 'undefined') return

    // The measurement above happens ONCE, and the panel grows AFTERWARDS — which is how Hang up
    // went off-screen. At the moment the ref attaches the call is still ringing, so
    // `SosCallPanel`'s `active` is false and the Mute / settings / Hang up row has not been
    // rendered at all. When the call connects that row appears (and the mic-silent banner can
    // appear later still), the panel grows downward, and nothing re-clamped it because the WINDOW
    // never resized. Measured on a live emergency call: header, video and "Connected" on screen,
    // the controls row below the fold, and no way to end the call short of reloading the page.
    //
    // Watching the panel itself covers every cause at once — the controls row, the banner, a
    // longer status line, a translation that wraps — instead of special-casing the reported one.
    //
    // It lives HERE rather than in a mount effect because this is where the element's lifetime
    // actually is: a `[]` effect runs with `elRef.current` still null whenever the ref attaches
    // later, and would silently observe nothing.
    //
    // Re-clamping changes the panel's POSITION, never its size, so this cannot feed back into a
    // loop. It is the same idempotent clamp the drag and window-resize paths use, so it is safe to
    // let it run mid-drag rather than racing a guard against it.
    const observer = new ResizeObserver(() => {
      setPos(p => (p ? clampToViewport(p, size(), viewport()) : p))
    })

    observer.observe(el)
    observerRef.current = observer
  }, [])

  // React does not call a ref callback with null on every unmount path in every version, so the
  // disconnect is belt-and-braces: an observer left alive holds the detached panel node.
  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    [],
  )

  // A resized window must not strand the panel where nobody can reach Hang up.
  useEffect(() => {
    const onResize = () => setPos(p => (p ? clampToViewport(p, size(), viewport()) : p))

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [])

  const onPointerDown = useCallback((e: PointerEvent<HTMLElement>) => {
    // The header also carries Dismiss. A drag that starts on a button would swallow its click.
    if ((e.target as HTMLElement).closest('button')) return

    const el = elRef.current

    if (!el) return

    const rect = el.getBoundingClientRect()

    grabRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    setDragging(true)
    // Capture on the HANDLE: the move and up events keep arriving even when the pointer outruns
    // the panel or leaves the window entirely.
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
  }, [])

  useEffect(() => {
    if (!dragging) return

    const onMove = (e: globalThis.PointerEvent) => {
      const next = { left: e.clientX - grabRef.current.dx, top: e.clientY - grabRef.current.dy }

      setPos(clampToViewport(next, size(), viewport()))
    }

    const onUp = () => {
      setDragging(false)
      setPos(p => {
        if (!p) return p

        const snapped = snapToEdge(p, size(), viewport())

        writeStored(snapped)

        return snapped
      })
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)

    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [dragging])

  return {
    panelRef,
    style: pos
      ? {
          left: pos.left,
          top: pos.top,
          right: 'auto',
          bottom: 'auto',
          transition: dragging ? 'none' : 'left 120ms ease-out, top 120ms ease-out',
        }
      : {},
    handleProps: {
      onPointerDown,
      // `touchAction: none` or the browser scrolls the page instead of giving us the drag.
      style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' },
    },
    dragging,
  }
}
